
import { useState, useEffect, useCallback } from 'react';
import { Room } from '@/types/chat';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export const useRooms = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const fetchRooms = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Get all rooms where the current user is a participant
      const { data: participantRooms, error: participantError } = await supabase
        .from('chat_room_participants')
        .select('room_id')
        .eq('user_id', user.id);

      if (participantError) {
        console.error('Error fetching rooms:', participantError);
        setIsLoading(false);
        return;
      }

      // Get detailed info for each room
      if (participantRooms && participantRooms.length > 0) {
        const roomIds = participantRooms.map(room => room.room_id);
        
        const { data: roomsData, error: roomsError } = await supabase
          .from('chat_rooms')
          .select(`
            id,
            name,
            created_by,
            is_group,
            last_message,
            last_message_at,
            created_at
          `)
          .in('id', roomIds)
          .order('last_message_at', { ascending: false, nullsFirst: false });

        if (roomsError) {
          console.error('Error fetching room details:', roomsError);
          setIsLoading(false);
          return;
        }

        // Get startup details for group rooms
        const startupRooms = roomsData.filter(room => room.is_group);
        let startupDetails = {};
        
        if (startupRooms.length > 0) {
          // Get startup names from room names (format: "Startup: {name}")
          const startupNames = startupRooms
            .map(room => room.name?.replace('Startup: ', ''))
            .filter(Boolean);
            
          if (startupNames.length > 0) {
            const { data: startups, error: startupsError } = await supabase
              .from('startups')
              .select('id, name, banner_url')
              .in('name', startupNames);
              
            if (!startupsError && startups) {
              // Create a map of startup name to startup details
              startupDetails = startups.reduce((acc, startup) => {
                acc[startup.name] = startup;
                return acc;
              }, {});
            }
          }
        }

        // For each room, get participants
        const roomsWithParticipants = await Promise.all(roomsData.map(async (room) => {
          const { data: participants, error: participantsError } = await supabase
            .from('chat_room_participants')
            .select(`
              user_id,
              joined_at,
              profiles:profiles(
                username,
                avatar_url
              )
            `)
            .eq('room_id', room.id)
            .neq('user_id', user.id);

          if (participantsError) {
            console.error('Error fetching participants:', participantsError);
            return {
              ...room,
              participants: []
            };
          }

          // Transform the data to match our types
          const transformedParticipants = participants.map(p => {
            let username: string | undefined;
            let avatarUrl: string | undefined;
            
            // Handle the profiles data which could be in different formats
            if (p.profiles) {
              if (Array.isArray(p.profiles) && p.profiles.length > 0) {
                // When profiles is an array
                username = p.profiles[0]?.username;
                avatarUrl = p.profiles[0]?.avatar_url;
              } else if (typeof p.profiles === 'object') {
                // When profiles is a single object
                username = (p.profiles as any).username;
                avatarUrl = (p.profiles as any).avatar_url;
              }
            }
            
            return {
              user_id: p.user_id,
              joined_at: p.joined_at,
              profiles: {
                username,
                avatar_url: avatarUrl
              }
            };
          });

          // Add startup info for group chats
          let startup = undefined;
          if (room.is_group && room.name) {
            const startupName = room.name.replace('Startup: ', '');
            startup = startupDetails[startupName];
          }

          return {
            ...room,
            participants: transformedParticipants,
            startup
          };
        }));

        setRooms(roomsWithParticipants);
      } else {
        // User has no rooms yet
        setRooms([]);
      }

      // We'll try to create rooms but won't block the UI on this
      try {
        createStartupRooms().catch(e => console.error("Startup rooms creation error:", e));
        createFollowersRooms().catch(e => console.error("Follower rooms creation error:", e));
      } catch (err) {
        console.error("Error in room creation processes:", err);
      }

    } catch (error) {
      console.error('Unexpected error in useRooms:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const refreshRooms = useCallback(async () => {
    await fetchRooms();
  }, [fetchRooms]);

  const createStartupRooms = async () => {
    if (!user) return;

    try {
      // First, check if the user has any startups
      const { data: startups, error: startupsError } = await supabase
        .from('startups')
        .select('id, name, banner_url')
        .eq('user_id', user.id);
      
      if (startupsError) {
        console.error('Error fetching startups:', startupsError);
        return;
      }

      if (!startups?.length) {
        // Check if user is a team member of any startups
        const { data: teamMemberships, error: teamError } = await supabase
          .from('startup_team_members')
          .select('startup_id, startups(id, name, banner_url)')
          .eq('user_id', user.id);

        if (teamError) {
          console.error('Error fetching team memberships:', teamError);
          return;
        }

        // If user is a team member of any startups, ensure they're in the startup rooms
        if (teamMemberships?.length) {
          for (const membership of teamMemberships) {
            // Type fix: access the startups property correctly
            const startup = membership.startups;
            if (!startup) continue;

            // Type fix: ensure we have the correct types for startup properties
            const startupName = typeof startup === 'object' && 'name' in startup ? startup.name : null;
            if (!startupName) continue;

            // Check if room exists
            const { data: existingRooms, error: roomCheckError } = await supabase
              .from('chat_rooms')
              .select('id')
              .eq('name', `Startup: ${startupName}`)
              .eq('is_group', true);

            if (roomCheckError) {
              console.error('Error checking for existing startup room:', roomCheckError);
              continue;
            }

            if (existingRooms?.length) {
              // Room exists, check if user is a participant
              const roomId = existingRooms[0].id;
              const { data: participation, error: participationError } = await supabase
                .from('chat_room_participants')
                .select('*')
                .eq('room_id', roomId)
                .eq('user_id', user.id);

              if (participationError) {
                console.error('Error checking participation:', participationError);
                continue;
              }

              if (!participation?.length) {
                // Add user as participant
                await supabase
                  .from('chat_room_participants')
                  .insert({
                    room_id: roomId,
                    user_id: user.id
                  });
              }
            }
          }
        }
        
        return;
      }

      // For each startup, check if a room already exists
      for (const startup of startups) {
        // Check if room exists
        const { data: existingRooms, error: roomCheckError } = await supabase
          .from('chat_rooms')
          .select('id')
          .eq('name', `Startup: ${startup.name}`)
          .eq('created_by', user.id)
          .eq('is_group', true);
        
        if (roomCheckError) {
          console.error('Error checking for existing startup room:', roomCheckError);
          continue;
        }

        // If room doesn't exist, create it
        if (!existingRooms?.length) {
          // Create a new room for this startup
          const { data: newRoom, error: createRoomError } = await supabase
            .from('chat_rooms')
            .insert({
              name: `Startup: ${startup.name}`,
              created_by: user.id,
              is_group: true
            })
            .select()
            .single();
          
          if (createRoomError) {
            console.error('Error creating startup room:', createRoomError);
            continue;
          }

          // Add the user as a participant
          const { error: participantError } = await supabase
            .from('chat_room_participants')
            .insert({
              room_id: newRoom.id,
              user_id: user.id
            });

          if (participantError) {
            console.error('Error adding user to startup room:', participantError);
          }

          // Get team members for this startup
          const { data: teamMembers, error: teamError } = await supabase
            .from('startup_team_members')
            .select('user_id')
            .eq('startup_id', startup.id)
            .neq('user_id', user.id);
          
          if (teamError) {
            console.error('Error getting team members:', teamError);
            continue;
          }

          // Add all team members to the room
          if (teamMembers?.length) {
            const participantsData = teamMembers.map(member => ({
              room_id: newRoom.id,
              user_id: member.user_id
            }));

            const { error: bulkInsertError } = await supabase
              .from('chat_room_participants')
              .insert(participantsData);

            if (bulkInsertError) {
              console.error('Error adding team members to room:', bulkInsertError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error creating startup rooms:', error);
    }
  };

  const createFollowersRooms = async () => {
    try {
      // Get followers (people following the current user)
      const { data: followers, error: followersError } = await supabase
        .from('user_follows')
        .select(`
          follower_id,
          profiles!follower_id(
            id,
            username,
            avatar_url
          )
        `)
        .eq('following_id', user?.id);
      
      if (followersError || !followers?.length) {
        return;
      }

      // For each follower, check if a direct message room exists
      for (const follower of followers) {
        if (!follower.follower_id || follower.follower_id === user?.id) continue;

        // Check for existing room between these two users
        const { data: existingRooms1, error: roomCheck1Error } = await supabase
          .from('chat_room_participants')
          .select('room_id')
          .eq('user_id', user?.id);
        
        if (roomCheck1Error) {
          console.error('Error checking for existing rooms:', roomCheck1Error);
          continue;
        }

        if (!existingRooms1?.length) continue;

        const { data: existingRooms2, error: roomCheck2Error } = await supabase
          .from('chat_room_participants')
          .select('room_id')
          .eq('user_id', follower.follower_id)
          .in('room_id', existingRooms1.map(r => r.room_id));
        
        if (roomCheck2Error) {
          console.error('Error checking for existing rooms:', roomCheck2Error);
          continue;
        }

        // If they already have a common room, skip
        if (existingRooms2?.length) continue;

        // Create a new room for direct messaging
        const { data: newRoom, error: createRoomError } = await supabase
          .from('chat_rooms')
          .insert({
            created_by: user?.id,
            is_group: false
          })
          .select()
          .single();
        
        if (createRoomError) {
          console.error('Error creating follower room:', createRoomError);
          continue;
        }

        // Add both users as participants
        await supabase
          .from('chat_room_participants')
          .insert([
            {
              room_id: newRoom.id,
              user_id: user?.id
            },
            {
              room_id: newRoom.id,
              user_id: follower.follower_id
            }
          ]);
      }
    } catch (error) {
      console.error('Error creating follower rooms:', error);
    }
  };

  useEffect(() => {
    fetchRooms();

    // Set up real-time subscription for changes to the chat_rooms table
    const roomsSubscription = supabase
      .channel('chat_rooms_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'chat_rooms'
        }, 
        () => {
          fetchRooms();
        }
      )
      .subscribe();

    // Set up real-time subscription for changes to the messages
    const messagesSubscription = supabase
      .channel('chat_messages_changes')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'chat_messages'
        }, 
        () => {
          fetchRooms();
        }
      )
      .subscribe();

    // Set up real-time subscription for chat room participants
    const participantsSubscription = supabase
      .channel('chat_participants_changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_room_participants'
        },
        () => {
          fetchRooms();
        }
      )
      .subscribe();

    // Set up real-time subscription for startup team members
    const teamMembersSubscription = supabase
      .channel('startup_team_members_changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'startup_team_members'
        },
        () => {
          // Don't block UI on this subscription
          createStartupRooms().then(() => fetchRooms()).catch(e => {
            console.error("Error handling team member changes:", e);
            fetchRooms();
          });
        }
      )
      .subscribe();

    return () => {
      roomsSubscription.unsubscribe();
      messagesSubscription.unsubscribe();
      participantsSubscription.unsubscribe();
      teamMembersSubscription.unsubscribe();
    };
  }, [fetchRooms]);

  return { rooms, isLoading, refreshRooms };
};
